import { Component, OnInit, inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { State } from '../shared/core/state';
import { User, UserProfileRequestDTO, UserRoles } from '../shared/core/state/users/models';
import { getResourcePath } from '../shared/util/helper.utils';

import * as UserActions from '../shared/core/state/users/user.actions';
import { mobileNumberValidator } from '../shared/util/form.validators';
import { ToastService } from '@black/ui';

@Component({
  selector: 'black-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css',
  standalone: false,
})
export class UserProfileComponent implements OnInit {
  UserRoles = UserRoles;
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private toast = inject(ToastService);

  isEditEnabled = false;


  updateProfileForm = this.fb.group({
    firstName: [''],
    lastName: [''],
    email: [''],
    mobile: ['', mobileNumberValidator],
    bio: [''],
    avatar: [''],
    addressLine1: [''],
    addressLine2: [''],
    city: [''],
    province: [''],
    postalCode: [''],
    country: [''],
  });

  user: Partial<User> | null = null;

  get isInstructorOrAdmin(): boolean {
    return (
      this.user?.role === UserRoles.INSTRUCTOR ||
      this.user?.role === UserRoles.ADMIN
    );
  }

  get hasIncompleteTutorProfile(): boolean {
    if (!this.isInstructorOrAdmin) return false;
    const tInfo = {
      ...(this.user?.profile?.tutorInfo || {}),
      ...(this.user?.pendingTutorProfileUpdates?.tutorInfo || {}),
    };
    const bio =
      this.user?.pendingTutorProfileUpdates?.personal?.biography ||
      this.user?.profile?.personal?.biography;
    return (
      !tInfo ||
      !tInfo.subjects?.length ||
      !tInfo.whatsAppNumber ||
      !bio ||
      bio.trim() === ''
    );
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.users.user)
      .subscribe((user) => {
        this.user = user;
        if (user) this.restoreFormData(user);
        this.updateProfileForm.disable();
      });
  }

  togglePageEdit(enable = true) {
    if (enable) {
      this.isEditEnabled = true;
      this.updateProfileForm.enable();
    } else {
      this.isEditEnabled = false;
      this.updateProfileForm.disable();
      // Restore original form data when canceling
      if (this.user) {
        this.restoreFormData(this.user);
      }
    }
  }

  restoreFormData(user: Partial<User>) {
    this.updateProfileForm.setValue({
      firstName: user.profile?.personal.firstName ?? '',
      lastName: user.profile?.personal.lastName ?? '',
      email: user.profile?.personal.email ?? '',
      mobile: user.profile?.personal.mobile ?? '',
      bio: user.profile?.personal.biography ?? '',
      avatar: user.profile?.personal.avatar?._id ?? '',
      addressLine1: user.profile?.address?.addressLine1 ?? '',
      addressLine2: user.profile?.address?.addressLine2 ?? '',
      city: user.profile?.address?.city ?? '',
      province: user.profile?.address?.province ?? '',
      postalCode: user.profile?.address?.postalCode ?? '',
      country: user.profile?.address?.country ?? '',
    });
  }

  avatarUploadSuccessHandler(resourceId: string) {
    this.updateProfileForm.controls.avatar.setValue(resourceId);
  }

  updateProfile() {
    if (this.updateProfileForm.valid) {
      const userProfileFormData = this.updateProfileForm.value;

      const userProfileRequestDTO: UserProfileRequestDTO = {
        firstName: userProfileFormData.firstName,
        lastName: userProfileFormData.lastName,
        email: userProfileFormData.email,
        mobile: userProfileFormData.mobile,
        biography: userProfileFormData.bio,
        avatar: userProfileFormData.avatar,
        addressLine1: userProfileFormData.addressLine1,
        addressLine2: userProfileFormData.addressLine2,
        city: userProfileFormData.city,
        province: userProfileFormData.province,
        postalCode: userProfileFormData.postalCode,
        country: userProfileFormData.country,
      };

      this.store.dispatch(
        UserActions.updateUserProfile({
          userProfile: userProfileRequestDTO,
          redirectOnSuccess: false,
        })
      );

      this.togglePageEdit(false);
    } else {
      this.toast.showToast('Form is invalid!', 'error');
    }
  }

  getAvatarUrl(avatar: any): string | null {
    if (!avatar) return null;
    return getResourcePath(avatar) || null;
  }

  getBannerUrl(banner: any): string | null {
    if (!banner) return null;
    return getResourcePath(banner) || null;
  }
}
